import {
  TextField,
  Grid,
  Autocomplete,
  FormControlLabel,
  MenuItem,
  FormControl,
  RadioGroup,
  FormLabel,
  Radio,
  InputAdornment,
  Checkbox,
  Card,
  CardHeader,
  CardContent,
  FormGroup,
  Tooltip,
  Stack,
} from "@mui/material"
import { forwardRef, useImperativeHandle, memo, useMemo, useEffect } from "react"

import { useFormik } from "formik"
import * as Yup from "yup"
import { useStores } from "stores/root-store"
import { rootStore } from "stores/root-store"

const InventoryProductInfo = forwardRef(({ productInformation, creatingBOM }, ref) => {
  const { InventoryStore } = useStores()
  const isEditing = Boolean(InventoryStore.productInformation)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: productInformation,
    validationSchema: Yup.object({
      product: Yup.string()
        .max(25)
        .required("Product is required")
        .test("testProduct", "Product already exists", (val, obj) => isEditing || !val || !rootStore.isProductAvailable(val.toUpperCase())),
      packing_class: Yup.string().required("Packing Class is required"),
      item_category1: Yup.string().required("Item Category 1 is required"),
      product_description: Yup.string().max(100).required("Product Description is required"),
      dg_volume: Yup.number().test("testDGVolume", "DG Volume is required", (val, obj) => !obj.parent.dgGoods || val),
      un_number: Yup.string()
        .max(6)
        .test("testUNNumber", "UN Number is required", (val, obj) => !obj.parent.dgGoods || val),
    }),
    onSubmit: async values => {
      //console.log(values)
    },
  })

  useImperativeHandle(ref, () => ({
    async getFormDataIfValid() {
      const val = await formik.validateForm()
      await formik.submitForm()

      if (!val || Object.keys(val).length == 0) return formik.values
      return null
    },
  }))

  useEffect(() => {
    if (creatingBOM) formik.setFieldValue("item_category1", "B")
  }, [])

  const handleChange = event => {
    formik.setFieldValue("dgGoods", event.target.checked)

    if (!event.target.checked) {
      formik.setFieldValue("dg_volume", 0)
      formik.setFieldValue("un_number", "")
    }
  }

  const handleSerialTrackedChange = event => {
    let serial_tracked = (formik.values["serial_tracked"] || "").replace("No", "")
    const checked = event.target.checked
    const val = event.target.value
    if (checked) serial_tracked = serial_tracked + val
    else serial_tracked = serial_tracked.replace(val, "")

    formik.setFieldValue("serial_tracked", serial_tracked)
  }

  const countries = useMemo(() => rootStore.getOptionsByFilter("COUNTRY"), [])
  return (
    <form onSubmit={formik.handleSubmit}>
      <Card>
        <CardHeader title="Product Information" />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                error={Boolean(formik.touched.product && formik.errors.product)}
                helperText={formik.touched.product && formik.errors.product}
                label="Product"
                name="product"
                id="product"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                required
                placeholder="product / SKU code"
                value={formik.values.product}
                variant="outlined"
                inputProps={{ maxLength: 25 }}
                InputProps={{
                  readOnly: isEditing,
                }}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="do serial numbers need to be captured for this SKU **speak to your account manager before using">
                <FormControl>
                  <FormLabel id="serial_tracked">Serial Number Tracking</FormLabel>
                  <FormGroup row aria-labelledby="serial_tracked">
                    <FormControlLabel
                      value="Inbound"
                      control={<Checkbox id="serial_tracked_inbound" checked={formik.values.serial_tracked?.indexOf("Inbound") >= 0} onChange={handleSerialTrackedChange} />}
                      label="INBOUND"
                    />
                    <FormControlLabel
                      value="Outbound"
                      control={<Checkbox id="serial_tracked_outbound" checked={formik.values.serial_tracked?.indexOf("Outbound") >= 0} onChange={handleSerialTrackedChange} />}
                      label="OUTBOUND"
                    />
                  </FormGroup>
                </FormControl>
              </Tooltip>
            </Grid>
            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="detailed product description of colour type size and any unique attributes">
                <TextField
                  fullWidth
                  label="Description"
                  name="product_description"
                  id="product_description"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.product_description}
                  variant="outlined"
                  inputProps={{ maxLength: 100 }}
                  multiline
                  placeholder="product description"
                  rows={2}
                  error={Boolean(formik.touched.product_description && formik.errors.product_description)}
                  helperText={formik.touched.product_description && formik.errors.product_description}
                  required
                />
              </Tooltip>
            </Grid>
            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="do batch numbers and expiry dates need to be captured for this SKU **speak to your account manager before using">
                <FormControl>
                  <FormLabel id="lot_controlled">Lot Controlled</FormLabel>
                  <RadioGroup row aria-labelledby="lot_controlled" name="lot_controlled" id="lot_controlled" value={formik.values.lot_controlled || ""} onChange={formik.handleChange}>
                    <FormControlLabel value="" control={<Radio />} label="None" />
                    <FormControlLabel value="Y" control={<Radio />} label="YES" />
                    <FormControlLabel value="N" control={<Radio />} label="NO" />
                  </RadioGroup>
                </FormControl>
              </Tooltip>
            </Grid>
            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="defines which containers will be used to pack this order">
                <TextField
                  error={Boolean(formik.touched.packing_class && formik.errors.packing_class)}
                  fullWidth
                  helperText={formik.touched.packing_class && formik.errors.packing_class}
                  select
                  required
                  label="Packing Class"
                  name="packing_class"
                  id="packing_class"
                  onChange={formik.handleChange}
                  value={formik.values.packing_class || ""}
                  variant="outlined"
                >
                  {rootStore.getOptionsByFilter("PACKING_CLASS").map(option => (
                    <MenuItem id={`packing_class-${option.value}`} key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Tooltip>
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                error={Boolean(formik.touched.item_category1 && formik.errors.item_category1)}
                fullWidth
                helperText={formik.touched.item_category1 && formik.errors.item_category1}
                select
                required
                label="Item Category 1"
                name="item_category1"
                id="item_category1"
                onChange={formik.handleChange}
                value={formik.values.item_category1 || ""}
                variant="outlined"
                InputProps={{
                  readOnly: creatingBOM,
                }}
              >
                {rootStore.getOptionsByFilter("ITEM_CATEGORY1").map(option => (
                  <MenuItem id={`item_category1-${option.value}`} key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="product's country of manufacture">
                <Autocomplete
                  disableClearable
                  fullWidth
                  name="country_of_origin"
                  id="country_of_origin"
                  value={rootStore.getTextByValue("COUNTRY", formik.values.country_of_origin)}
                  onChange={(event, item) => formik.setFieldValue("country_of_origin", item.id)}
                  options={countries.map(option => ({ label: `${option.value} - ${option.label}`, id: option.value }))}
                  renderInput={params => <TextField {...params} label="Country of Origin" />}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                />
              </Tooltip>
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                select
                label="Item Category 2"
                name="item_category2"
                id="item_category2"
                onChange={formik.handleChange}
                value={formik.values.item_category2 || ""}
                variant="outlined"
              >
                {[
                  {
                    label: "Select",
                    value: "",
                  },
                  ...rootStore.getOptionsByFilter("ITEM_CATEGORY2"),
                ].map(option => (
                  <MenuItem id={`item_category2-${option.value}`} key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="harmonised code used for product export">
                <Autocomplete
                  disableClearable
                  fullWidth
                  name="harmonized_code"
                  id="harmonized_code"
                  value={formik.values.harmonized_code || ""}
                  onChange={(event, item) => formik.setFieldValue("harmonized_code", item.id)}
                  options={rootStore.getOptionsByFilter("HARMONIZED_CODE").map(option => ({ label: option.label, id: option.value }))}
                  renderInput={params => <TextField {...params} label="Harmonized Code" />}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                />
              </Tooltip>
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                select
                label="Item Category 3"
                name="item_category3"
                id="item_category3"
                onChange={formik.handleChange}
                value={formik.values.item_category3 || ""}
                variant="outlined"
              >
                {[
                  {
                    label: "Select",
                    value: "",
                  },
                  ...rootStore.getOptionsByFilter("ITEM_CATEGORY3"),
                ].map(option => (
                  <MenuItem id={`item_category3-${option.value}`} key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                label="List Price"
                name="list_price"
                id="list_price"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.list_price}
                variant="outlined"
                type="number"
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <FormControlLabel id="dgGoods" control={<Checkbox checked={formik.values.dgGoods} onChange={handleChange} inputProps={{ "aria-label": "controlled" }} />} label="Dangerous Good" />
            </Grid>

            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="export description for the item">
                <TextField
                  fullWidth
                  label="Long Description"
                  name="long_description"
                  id="long_description"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.long_description}
                  variant="outlined"
                  inputProps={{ maxLength: 100 }}
                  multiline
                  rows={4}
                />
              </Tooltip>
            </Grid>
            <Grid item md={6} xs={12}>
              <Stack spacing={1.5}>
                {formik.values.dgGoods && (
                  <Tooltip placement="top" title="total amount of dangerous goods volume per piece, in Litres">
                    <TextField
                      error={Boolean(formik.touched.dg_volume && formik.errors.dg_volume)}
                      fullWidth
                      helperText={formik.touched.dg_volume && formik.errors.dg_volume}
                      label="DG Volume"
                      name="dg_volume"
                      id="dg_volume"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.dg_volume}
                      variant="outlined"
                      type="number"
                    />
                  </Tooltip>
                )}
                {formik.values.dgGoods && (
                  <Tooltip placement="top" title="dangerous goods UN number">
                    <TextField
                      error={Boolean(formik.touched.un_number && formik.errors.un_number)}
                      fullWidth
                      helperText={formik.touched.un_number && formik.errors.un_number}
                      label="UN Number"
                      name="un_number"
                      id="un_number"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.un_number}
                      variant="outlined"
                    />
                  </Tooltip>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </form>
  )
})

export default memo(InventoryProductInfo)
